import { type EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { useEffect, useMemo } from "react";
import { canOpen } from "@/routes/environments/phase";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useEnvironmentSettings } from "./use-environment-settings";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useListServices } from "@/queries/automations-queries";
import { ServicePhase, ServiceRole } from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useFlagIsMobileVscodeEnabled } from "@/hooks/use-flag";

/**
 * Hook for managing view mode (details vs vscode) per environment
 * Persists the user's preferred view mode but enforces business rules
 * Automatically defaults to VS Code when environment is running, falls back to "details" when environment can't be opened
 */
export function useEnvironmentViewMode(
    environment?: PlainEnvironment,
): [EnvironmentViewMode, (mode: EnvironmentViewMode) => void] {
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();

    // Mobile should block VSCode only when the feature flag is disabled
    const shouldBlockVscodeOnMobile = isMobile && !isMobileVscodeEnabled;

    // Check if VS Code is actually connected by looking for running editor service
    const { data: services } = useListServices(environment?.id);
    const isVSCodeConnected = useMemo(() => {
        return (
            services?.some(
                (service) =>
                    service.status?.phase === ServicePhase.RUNNING && service.metadata?.role === ServiceRole.EDITOR,
            ) ?? false
        );
    }, [services]);

    // Determine smart default based on environment state and VS Code connection
    const smartDefault = useMemo((): EnvironmentViewMode => {
        if (!environment) return "details";

        // Force "details" mode when in VS Code context (embedded webview) or on mobile (when feature flag is disabled)
        if (window.isWebview || shouldBlockVscodeOnMobile) {
            return "details";
        }

        const actual = environment.status?.phase ?? EnvironmentPhase.UNSPECIFIED;

        // Default to VS Code when environment is running AND VS Code is connected (respecting mobile feature flag)
        if (actual === EnvironmentPhase.RUNNING && canOpen(environment) && isVSCodeConnected) {
            return "vscode";
        }

        return "details";
    }, [environment, isVSCodeConnected, shouldBlockVscodeOnMobile]);

    const { value: viewMode, setValue: setViewMode } = useEnvironmentSettings(
        environment?.id ?? "__no_env__",
        "viewmode",
        smartDefault,
    );

    // Force "details" if environment can't be opened OR if in VS Code context OR on mobile (when feature flag is disabled)
    useEffect(() => {
        if (
            environment &&
            (!canOpen(environment) || window.isWebview || shouldBlockVscodeOnMobile) &&
            viewMode === "vscode"
        ) {
            setViewMode("details");
        }
    }, [environment, viewMode, setViewMode, shouldBlockVscodeOnMobile]);

    // Auto-switch to VS Code when environment becomes running (only if no explicit preference exists, not in VS Code context, and respecting mobile feature flag)
    useEffect(() => {
        if (!environment || window.isWebview || shouldBlockVscodeOnMobile) return;

        const actual = environment.status?.phase ?? EnvironmentPhase.UNSPECIFIED;
        const storageKey = `environment-settings:${environment.id}:viewmode`;
        const hasExplicitPreference = localStorage.getItem(storageKey) !== null;

        // Only auto-switch if no explicit preference exists, environment is running, VS Code is connected, not in VS Code context, and respecting mobile feature flag
        if (
            actual === EnvironmentPhase.RUNNING &&
            canOpen(environment) &&
            isVSCodeConnected &&
            !hasExplicitPreference &&
            viewMode === "details"
        ) {
            setViewMode("vscode");
        }
    }, [environment, viewMode, setViewMode, isVSCodeConnected, shouldBlockVscodeOnMobile]);

    return [viewMode as EnvironmentViewMode, setViewMode as (mode: EnvironmentViewMode) => void];
}
